import { useState, useRef, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import {
  MapPin, Car, Search, Loader2,
  CheckCircle2, AlertTriangle, RefreshCw,
  ArrowRight, TrendingUp, ExternalLink, Clock,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VEHICLE_TYPES = [
  { value: 'CAR',        label: 'Car / Sedan'      },
  { value: 'PICKUP',     label: 'Pickup Truck'     },
  { value: 'SUV',        label: 'SUV / Crossover'  },
  { value: 'VAN',        label: 'Van / Minivan'    },
  { value: 'MOTORCYCLE', label: 'Motorcycle'       },
  { value: 'RV',         label: 'RV / Motorhome'   },
  { value: 'ATV',        label: 'ATV / UTV'        },
  { value: 'BOAT',       label: 'Boat'             },
  { value: 'OTHER',      label: 'Other'            },
];

const FIELD = 'w-full border-b border-border bg-transparent py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-accent transition-colors';
const SELECT = 'w-full border-b border-border bg-card py-2 text-sm text-foreground focus:outline-none focus:border-accent transition-colors';

function ZipBlock({ heading, accentColor, zipValue, onZipChange, street, onStreetChange, loc, loading, error }) {
  return (
    <div className="bg-card border border-border rounded-xl p-5 space-y-4">
      <div className="flex items-center gap-2">
        <MapPin className={`h-4 w-4 ${accentColor}`} />
        <h2 className="font-semibold text-foreground font-outfit">{heading}</h2>
      </div>

      {/* ZIP */}
      <div className="space-y-1">
        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">ZIP Code *</label>
        <div className="relative">
          <input
            type="text"
            value={zipValue}
            onChange={e => onZipChange(e.target.value.replace(/\D/g, '').slice(0, 10))}
            placeholder="e.g. 75063"
            className={FIELD}
          />
          {loading  && <Loader2     className="absolute right-0 top-2 h-4 w-4 animate-spin text-muted-foreground" />}
          {!loading && loc   && <CheckCircle2  className="absolute right-0 top-2 h-4 w-4 text-green-500" />}
          {!loading && error && <AlertTriangle className="absolute right-0 top-2 h-4 w-4 text-destructive" />}
        </div>
        {error && <p className="text-xs text-destructive">{error}</p>}
      </div>

      {/* Auto-filled location */}
      {loc && (
        <div className="grid grid-cols-3 gap-3 bg-muted/30 rounded-lg px-3 py-2">
          {[['City', loc.city], ['State', loc.state], ['Country', loc.country]].map(([k, v]) => (
            <div key={k}>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-0.5">{k}</p>
              <p className="text-sm font-semibold text-foreground">{v}</p>
            </div>
          ))}
        </div>
      )}

      {/* Street (optional) */}
      <div className="space-y-1">
        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Street Address (optional)</label>
        <input
          type="text"
          value={street}
          onChange={e => onStreetChange(e.target.value)}
          placeholder="e.g. 8440 Freeport Parkway"
          className={FIELD}
        />
      </div>
    </div>
  );
}

const STATUS_STYLES = {
  NOT_SIGNED:       'bg-yellow-500/15 text-yellow-400 border-yellow-500/30',
  PENDING_PICKUP:   'bg-blue-500/15 text-blue-400 border-blue-500/30',
  PENDING_DELIVERY: 'bg-purple-500/15 text-purple-400 border-purple-500/30',
  DELIVERED:        'bg-green-500/15 text-green-400 border-green-500/30',
  CANCELED:         'bg-red-500/15 text-red-400 border-red-500/30',
};

function StatusBadge({ status }) {
  const cls = STATUS_STYLES[status] || 'bg-muted/40 text-muted-foreground border-border';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${cls}`}>
      {status?.replace(/_/g, ' ')}
    </span>
  );
}

function fmt(n) { return n != null ? `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'; }
function fmtMi(n) { return n != null ? `$${Number(n).toFixed(2)}/mi` : '—'; }

function PriceResult({ result }) {
  const items = result?.items ?? [];
  const first = items[0];

  // Shared prediction (same across items in this response)
  const low  = first?.lowPrice;
  const high = first?.highPrice;
  const mean = first?.meanPredictedPrice;
  const ttd  = first?.timeToDispatchForMeanPredictedPrice;

  return (
    <div className="space-y-4">

      {/* Summary header */}
      <div className="bg-card border border-border rounded-xl p-5 space-y-4">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-accent" />
          <h2 className="font-semibold text-foreground font-outfit">Market Price Prediction</h2>
          <span className="ml-auto text-xs text-muted-foreground">{result?.count ?? items.length} comparable listings</span>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div className="bg-muted/30 rounded-lg p-3 text-center">
            <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1">Low</p>
            <p className="text-xl font-bold text-foreground">{fmt(low)}</p>
          </div>
          <div className="bg-accent/10 border border-accent/20 rounded-lg p-3 text-center">
            <p className="text-[10px] text-accent uppercase tracking-wide mb-1">Mean Predicted</p>
            <p className="text-xl font-bold text-accent">{fmt(mean)}</p>
          </div>
          <div className="bg-muted/30 rounded-lg p-3 text-center">
            <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1">High</p>
            <p className="text-xl font-bold text-foreground">{fmt(high)}</p>
          </div>
        </div>

        {ttd && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/20 rounded-lg px-3 py-2">
            <Clock className="h-3.5 w-3.5 shrink-0" />
            <span>Time to dispatch at mean price — min <strong className="text-foreground">{ttd.minHoursToDispatch}h</strong>, expected <strong className="text-foreground">{ttd.expectedHoursToDispatch}h</strong>, max <strong className="text-foreground">{ttd.maxHoursToDispatch}h</strong></span>
          </div>
        )}
      </div>

      {/* Comparable listing cards */}
      {items.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide px-1">Comparable Listings</h3>
          {items.map((item, i) => (
            <div key={i} className="bg-card border border-border rounded-xl p-4 space-y-3">
              {/* Route row */}
              <div className="flex items-center gap-2 text-sm">
                <MapPin className="h-3.5 w-3.5 text-accent shrink-0" />
                <span className="font-medium text-foreground">{item.originCity}, {item.originState} {item.originZipCode}</span>
                <ArrowRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <span className="font-medium text-foreground">{item.destinationCity}, {item.destinationState} {item.destinationZipCode}</span>
                <span className="ml-auto text-xs text-muted-foreground">{item.dispatchDistance} {item.dispatchDistanceUnits}</span>
              </div>

              {/* Price grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-0.5">Listing Price</p>
                  <p className="text-base font-bold text-foreground">{fmt(item.listingPrice)}</p>
                  <p className="text-[10px] text-muted-foreground">{fmtMi(item.listingPricePerMile)}</p>
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-0.5">Dispatch Price</p>
                  <p className="text-base font-bold text-accent">{fmt(item.dispatchPrice)}</p>
                  <p className="text-[10px] text-muted-foreground">{fmtMi(item.dispatchPricePerMile)}</p>
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-0.5">Vehicle</p>
                  <p className="text-sm font-semibold text-foreground">{item.vehicleType}</p>
                  <p className="text-[10px] text-muted-foreground">{item.vehicleSize} · {item.trailerType}</p>
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-0.5">Status</p>
                  <StatusBadge status={item.dispatchStatus} />
                  {item.dispatchDate && (
                    <p className="text-[10px] text-muted-foreground mt-1">
                      {new Date(item.dispatchDate).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </div>

              {/* Link */}
              {item.href && (
                <a
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-accent transition-colors"
                >
                  <ExternalLink className="h-3 w-3" /> View listing on Manheim
                </a>
              )}
            </div>
          ))}
        </div>
      )}

    </div>
  );
}

export default function CDPriceCheckPage() {
  const { token } = useAuth();

  const [form, setForm] = useState({
    origin_zip:         '',
    destination_zip:    '',
    origin_street:      '',
    destination_street: '',
    vehicle_year:       '2024',
    vehicle_make:       'Tesla',
    vehicle_model:      'Model 3',
    vehicle_type:       'CAR',
    trailer_type:       'Open',
    is_operable:        true,
    vin:                '',
  });

  const [originLoc,     setOriginLoc]     = useState(null);
  const [destLoc,       setDestLoc]       = useState(null);
  const [originLoading, setOriginLoading] = useState(false);
  const [destLoading,   setDestLoading]   = useState(false);
  const [originError,   setOriginError]   = useState('');
  const [destError,     setDestError]     = useState('');

  const [submitting,   setSubmitting]   = useState(false);
  const [result,       setResult]       = useState(null);
  const [submitError,  setSubmitError]  = useState('');

  const originTimer = useRef(null);
  const destTimer   = useRef(null);

  const lookupZip = useCallback(async (zip, setLoc, setLoading, setError) => {
    setLoading(true);
    setError('');
    try {
      const res  = await fetch(`${API_URL}/api/cd-price-check/zip-location?zip=${zip}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setLoc(data.data);
      } else {
        setLoc(null);
        setError(data.detail || 'Invalid ZIP');
      }
    } catch {
      setLoc(null);
      setError('Lookup failed');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const handleOriginZip = (val) => {
    setForm(f => ({ ...f, origin_zip: val }));
    clearTimeout(originTimer.current);
    if (val.length >= 4) {
      originTimer.current = setTimeout(
        () => lookupZip(val, setOriginLoc, setOriginLoading, setOriginError), 600
      );
    } else {
      setOriginLoc(null);
      setOriginError('');
    }
  };

  const handleDestZip = (val) => {
    setForm(f => ({ ...f, destination_zip: val }));
    clearTimeout(destTimer.current);
    if (val.length >= 4) {
      destTimer.current = setTimeout(
        () => lookupZip(val, setDestLoc, setDestLoading, setDestError), 600
      );
    } else {
      setDestLoc(null);
      setDestError('');
    }
  };

  const set = (key) => (e) =>
    setForm(f => ({ ...f, [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.origin_zip || !form.destination_zip) {
      toast.error('Enter both origin and destination ZIP codes');
      return;
    }
    setSubmitting(true);
    setSubmitError('');
    setResult(null);
    try {
      const res  = await fetch(`${API_URL}/api/cd-price-check`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body:    JSON.stringify(form),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setResult(data.data);
        toast.success('Market price retrieved');
      } else {
        setSubmitError(data.detail || 'Price check failed');
        toast.error(data.detail || 'Price check failed');
      }
    } catch {
      setSubmitError('Request failed — check server connection');
      toast.error('Request failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">

      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold text-foreground font-outfit">CD Market Price Check</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Enter ZIP codes — city, state, and country auto-fill. Then submit to get live Central Dispatch market pricing.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">

        {/* Origin + Destination side by side */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <ZipBlock
            heading="Origin"
            accentColor="text-accent"
            zipValue={form.origin_zip}
            onZipChange={handleOriginZip}
            street={form.origin_street}
            onStreetChange={v => setForm(f => ({ ...f, origin_street: v }))}
            loc={originLoc}
            loading={originLoading}
            error={originError}
          />
          <ZipBlock
            heading="Destination"
            accentColor="text-green-500"
            zipValue={form.destination_zip}
            onZipChange={handleDestZip}
            street={form.destination_street}
            onStreetChange={v => setForm(f => ({ ...f, destination_street: v }))}
            loc={destLoc}
            loading={destLoading}
            error={destError}
          />
        </div>

        {/* Vehicle details */}
        <div className="bg-card border border-border rounded-xl p-5 space-y-4">
          <div className="flex items-center gap-2">
            <Car className="h-4 w-4 text-accent" />
            <h2 className="font-semibold text-foreground font-outfit">Vehicle Details</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Year</label>
              <input type="text" value={form.vehicle_year}  onChange={set('vehicle_year')}  placeholder="2024"    className={FIELD} />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Make</label>
              <input type="text" value={form.vehicle_make}  onChange={set('vehicle_make')}  placeholder="Tesla"   className={FIELD} />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Model</label>
              <input type="text" value={form.vehicle_model} onChange={set('vehicle_model')} placeholder="Model 3" className={FIELD} />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Type</label>
              <select value={form.vehicle_type} onChange={set('vehicle_type')} className={SELECT}>
                {VEHICLE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Trailer</label>
              <select value={form.trailer_type} onChange={set('trailer_type')} className={SELECT}>
                <option value="Open">Open</option>
                <option value="Enclosed">Enclosed</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 items-end">
            <div className="space-y-1 md:col-span-2">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">VIN (optional)</label>
              <input type="text" value={form.vin} onChange={set('vin')} placeholder="5YJ3E1EA..." className={FIELD} />
            </div>
            <div className="flex items-center gap-3 pb-2">
              <input
                id="operable"
                type="checkbox"
                checked={form.is_operable}
                onChange={set('is_operable')}
                className="w-4 h-4 accent-accent"
              />
              <label htmlFor="operable" className="text-sm font-medium text-muted-foreground cursor-pointer">
                Vehicle is operable
              </label>
            </div>
          </div>
        </div>

        {/* Error */}
        {submitError && (
          <div className="flex items-start gap-2.5 px-4 py-3 bg-destructive/10 border border-destructive/20 rounded-lg text-sm text-destructive">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>{submitError}</span>
          </div>
        )}

        {/* Submit */}
        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={submitting || !form.origin_zip || !form.destination_zip}
            className="flex items-center gap-2 bg-accent text-accent-foreground rounded-full px-8 py-2.5 font-semibold text-sm hover:brightness-110 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            {submitting ? 'Checking...' : 'Get Market Price'}
          </button>
          {result && (
            <button
              type="button"
              onClick={() => { setResult(null); setSubmitError(''); }}
              className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <RefreshCw className="h-3.5 w-3.5" /> Clear
            </button>
          )}
        </div>
      </form>

      {/* Result */}
      {result && <PriceResult result={result} />}
    </div>
  );
}
